export interface Appearance {
  /** Classic is the broadsheet; Spot is the contemporary skin with its own blue
   *  ink and nameplate; Mono is grey paper and black ink in rounded cards, with
   *  the accent kept for a few small details. */
  style: 'classic' | 'spot' | 'mono';
  theme: 'light' | 'dark' | 'system';
  paper: 'cream' | 'white' | 'pink';
  accent: 'red' | 'navy' | 'black' | 'green';
  masthead: 'blackletter' | 'didone' | 'modern';
  density: 'airy' | 'balanced' | 'dense';
}

export const APPEARANCE_OPTIONS = {
  style: ['classic', 'spot', 'mono'],
  theme: ['light', 'dark', 'system'],
  paper: ['cream', 'white', 'pink'],
  accent: ['red', 'navy', 'black', 'green'],
  masthead: ['blackletter', 'didone', 'modern'],
  density: ['airy', 'balanced', 'dense'],
} as const;

export const DEFAULT_APPEARANCE: Appearance = {
  style: 'classic',
  theme: 'light',
  paper: 'cream',
  accent: 'red',
  masthead: 'blackletter',
  density: 'balanced',
};

export const STORAGE_KEY = 'hub:appearance';

type MatchMedia = (query: string) => { matches: boolean };

export function resolveTheme(
  theme: Appearance['theme'],
  matchMedia: MatchMedia = (q) => window.matchMedia(q),
): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  try {
    return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Sets the six data attributes on the root element and remembers the choice for the next first paint. */
export function applyTheme(a: Appearance, root: HTMLElement = document.documentElement): void {
  root.dataset.style = a.style;
  root.dataset.theme = resolveTheme(a.theme);
  root.dataset.paper = a.paper;
  root.dataset.accent = a.accent;
  root.dataset.masthead = a.masthead;
  root.dataset.density = a.density;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(a));
  } catch {
    // localStorage not available
  }
}

function pick<K extends keyof Appearance>(
  key: K,
  value: unknown,
): Appearance[K] {
  if (value && APPEARANCE_OPTIONS[key].includes(value as never)) {
    return value as Appearance[K];
  }
  return DEFAULT_APPEARANCE[key];
}

/** Normalises any object into a full Appearance, falling back per key. */
export function coerceAppearance(raw: unknown): Appearance {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    style: pick('style', o.style),
    theme: pick('theme', o.theme),
    paper: pick('paper', o.paper),
    accent: pick('accent', o.accent),
    masthead: pick('masthead', o.masthead),
    density: pick('density', o.density),
  };
}

export function readStoredAppearance(): Appearance | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return coerceAppearance(JSON.parse(raw));
  } catch {
    return null;
  }
}
